<template>
  <section
    class="testimonials-section"
    :class="{ 'cursor-hidden': isCursorVisible }"
    ref="sectionRef"
    @click="handleSectionClick"
  >
    <div class="testimonials-section__container">
      <div class="testimonials-section__header" ref="headerRef">
        <h2 class="testimonials-section__title">{{ $t('page.testimonials.title') }}</h2>
        <p class="testimonials-section__subtitle">
          {{ $t('page.testimonials.subtitle') }}
        </p>
      </div>

      <div class="testimonials-carousel" ref="carouselRef">
        <button
          type="button"
          class="carousel-arrow carousel-arrow--prev flex items-center justify-center lg:hidden"
          @click="prevTestimonial"
          :disabled="currentIndex === 0"
        >
          <ArrowLeft01Icon />
        </button>

        <div class="carousel-avatars">
          <button
            type="button"
            v-for="(testimonial, index) in testimonials"
            :key="testimonial.id"
            :ref="(el) => (avatarRefs[index] = el)"
            class="avatar-item"
            :class="{ 'avatar-item--active': currentIndex === index }"
            @click="goToTestimonial(index)"
          >
            <!-- Progress Ring SVG -->
            <svg class="avatar-progress-ring" viewBox="0 0 100 100">
              <circle class="avatar-progress-ring__circle" cx="50" cy="50" r="48" />
            </svg>

            <img
              v-if="testimonial.avatar"
              :src="testimonial.avatar"
              :alt="testimonial.name"
              class="avatar-item__image"
            />
            <span v-else class="avatar-item__initials">{{ testimonial.initials }}</span>
          </button>
        </div>

        <button
          type="button"
          class="carousel-arrow carousel-arrow--next flex items-center justify-center lg:hidden"
          @click="nextTestimonial"
          :disabled="currentIndex === testimonials.length - 1"
        >
          <ArrowRight01Icon />
        </button>
      </div>

      <div class="testimonials-content">
        <div class="testimonials-watermark" ref="watermarkRef">
          <img
            v-if="currentTestimonial.companyLogo"
            :src="currentTestimonial.companyLogo"
            :alt="currentTestimonial.company"
            class="watermark-logo"
          />
          <span v-else class="watermark-text">{{ currentTestimonial.company }}</span>
        </div>

        <div
          class="testimonial-quote"
          ref="testimonialCardRef"
          @mouseenter="pauseTimer"
          @mouseleave="resumeTimer"
        >
          <p class="testimonial-quote__text" ref="quoteRef">
            "{{ displayedQuote }}"
            <button
              type="button"
              v-if="isLongQuote"
              @click.stop="toggleExpand"
              class="read-more-btn"
              :aria-label="isExpanded ? 'Read less' : 'Read more'"
              @mouseenter="handleLinkHover(true)"
              @mouseleave="handleLinkHover(false)"
            >
              {{ isExpanded ? $t('common.readLess') : $t('common.readMore') }}
            </button>
          </p>

          <div class="testimonial-author" ref="authorRef">
            <template v-if="currentTestimonial.linkedin">
              <a
                :href="currentTestimonial.linkedin"
                target="_blank"
                rel="noopener noreferrer"
                class="testimonial-author__link"
                :aria-label="`Visit ${currentTestimonial.name}'s LinkedIn`"
                @mouseenter="handleLinkHover(true)"
                @mouseleave="handleLinkHover(false)"
              >
                <span class="testimonial-author__name">{{ currentTestimonial.name }}</span>
                <Linkedin01Icon class="w-4 h-4 ml-2" />
              </a>
            </template>
            <template v-else>
              <span class="testimonial-author__name">{{ currentTestimonial.name }}</span>
            </template>
            <span class="testimonial-author__separator">/</span>
            <span class="testimonial-author__role">{{ currentTestimonial.role }}</span>
          </div>
        </div>
      </div>
    </div>

    <div
      ref="cursorRef"
      class="custom-cursor"
      :class="{ 'custom-cursor--active': isCursorVisible }"
    >
      <div class="custom-cursor__icon">
        <component :is="cursorIcon" />
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'

import { ArrowLeft01Icon, ArrowRight01Icon, PauseIcon, Linkedin01Icon } from 'hugeicons-vue'

import { useI18n } from 'vue-i18n'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const { t, tm } = useI18n()

// Template refs
const sectionRef = ref(null)
const headerRef = ref(null)
const testimonialCardRef = ref(null)
const quoteRef = ref(null)
const authorRef = ref(null)
const watermarkRef = ref(null)
const carouselRef = ref(null)
const avatarRefs = ref([])
const cursorRef = ref(null)

const testimonials = computed(() => tm('page.testimonials.items'))

const currentIndex = ref(0)
const isPaused = ref(false)

let progressTween = null
let ctx = null

const pauseTimer = () => {
  isPaused.value = true
  if (progressTween) progressTween.pause()
}

const resumeTimer = () => {
  isPaused.value = false
  if (progressTween) progressTween.play()
}

const currentTestimonial = computed(() => testimonials.value[currentIndex.value])

// Calculate duration based on text length
const calculateDuration = (text) => {
  const baseTime = 3000
  const timePerChar = 70
  return baseTime + (text ? text.length * timePerChar : 0)
}

// Animación de auto-avance
const startAutoAdvance = () => {
  if (progressTween) progressTween.kill()

  const currentDuration = calculateDuration(currentTestimonial.value.quote)

  // Reset all rings first (optional, guarantees clean state)
  avatarRefs.value.forEach((btn) => {
    if (btn) {
      const ring = btn.querySelector('.avatar-progress-ring__circle')
      if (ring) {
        gsap.set(ring, {
          strokeDashoffset: 2 * Math.PI * 48,
          strokeDasharray: 2 * Math.PI * 48,
        })
      }
    }
  })

  // Animate active ring
  const activeBtn = avatarRefs.value[currentIndex.value]
  if (activeBtn) {
    const ring = activeBtn.querySelector('.avatar-progress-ring__circle')
    if (ring) {
      const circumference = 2 * Math.PI * 48

      progressTween = gsap.fromTo(
        ring,
        {
          strokeDashoffset: circumference,
          strokeDasharray: circumference,
        },
        {
          strokeDashoffset: 0,
          duration: currentDuration / 1000,
          ease: 'linear',
          onComplete: () => {
            if (!isPaused.value) {
              nextTestimonial()
            }
          },
        }
      )
      return
    }
  }

  // Fallback if no ring found
  progressTween = gsap.delayedCall(currentDuration / 1000, () => {
    if (!isPaused.value) {
      nextTestimonial()
    }
  })
}

// --- READ MORE LOGIC ---
const isExpanded = ref(false)
const MAX_QUOTE_LENGTH = 180 // Characters
// IDs of testimonials that should have "Read More" functionality (Baraka and Mariana)
const EXPANDABLE_IDS = [2, 4]

const displayedQuote = computed(() => {
  const quote = currentTestimonial.value?.quote || ''
  // If not in our target list, always show full
  if (!EXPANDABLE_IDS.includes(currentTestimonial.value?.id)) {
    return quote
  }

  if (isExpanded.value || quote.length <= MAX_QUOTE_LENGTH) {
    return quote
  }
  return quote.slice(0, MAX_QUOTE_LENGTH) + '...'
})

const isLongQuote = computed(() => {
  if (!EXPANDABLE_IDS.includes(currentTestimonial.value?.id)) {
    return false
  }
  return (currentTestimonial.value?.quote?.length || 0) > MAX_QUOTE_LENGTH
})

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value
  pauseTimer() // Pause if interacting
}

// --- ANIMATION LOGIC ---
const changeTestimonial = (newIndex) => {
  // Prevent re-triggering if same index or already animating (optional but safe)
  if (newIndex === currentIndex.value) return
  if (gsap.isTweening(quoteRef.value) || gsap.isTweening(headerRef.value)) return

  // Kill auto-advance timer
  if (progressTween) progressTween.kill()

  const tl = gsap.timeline({
    onComplete: () => {
      // 2. Update Data (DOM updates)
      currentIndex.value = newIndex
      isExpanded.value = false // Reset expanded state

      nextTick(() => {
        // 3. Fade In
        const tlIn = gsap.timeline()

        // Animate Text Elements In
        tlIn.fromTo(
          [quoteRef.value, authorRef.value],
          { opacity: 0, y: -20, filter: 'blur(8px)' },
          {
            opacity: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: 0.4,
            ease: 'power2.out',
            stagger: 0.1,
          }
        )

        // Animate Watermark In
        if (watermarkRef.value) {
          tlIn.fromTo(
            watermarkRef.value,
            { opacity: 0, scale: 1.2, filter: 'blur(4px)' },
            {
              opacity: 1,
              scale: 1,
              filter: 'blur(0px)',
              duration: 0.8,
              ease: 'power3.out',
            },
            '-=0.4'
          )
        }

        // Restart timer
        startAutoAdvance()
      })
    },
  })

  // 1. Fade Out
  tl.to([quoteRef.value, authorRef.value], {
    opacity: 0,
    y: 20,
    filter: 'blur(8px)',
    duration: 0.3,
    ease: 'power2.in',
    stagger: 0.05,
  })

  if (watermarkRef.value) {
    tl.to(
      watermarkRef.value,
      {
        opacity: 0,
        scale: 0.9,
        filter: 'blur(4px)',
        duration: 0.3,
        ease: 'power2.in',
      },
      0
    )
  }
}

// Updated Navigation Functions
const nextTestimonial = () => {
  let nextIndex = currentIndex.value + 1
  if (nextIndex >= testimonials.value.length) {
    nextIndex = 0
  }
  changeTestimonial(nextIndex)
}

const prevTestimonial = () => {
  let prevIndex = currentIndex.value - 1
  if (prevIndex < 0) {
    prevIndex = testimonials.value.length - 1
  }
  changeTestimonial(prevIndex)
}

const goToTestimonial = (index) => {
  changeTestimonial(index)
}

// Removed watch(currentIndex) since animation is now manual trigger
// Replaced animateContentTransition with changeTestimonial logic flow

// Removed scroll animation logic as requested

onMounted(() => {
  // GSAP Context for easy cleanup
  ctx = gsap.context(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.value,
        start: 'top 80%', // When top of section hits 80% of viewport
        end: 'bottom 20%', // When bottom of section hits 20% of viewport
        toggleActions: 'play reverse play reverse', // Enter, Leave, EnterBack, LeaveBack
        markers: false, // Set to true for debugging
      },
    })

    tl.from(headerRef.value, {
      y: 50,
      opacity: 0,
      duration: 0.8,
      ease: 'power3.out',
    })
      .from(
        carouselRef.value,
        {
          y: 50,
          opacity: 0,
          duration: 0.8,
          ease: 'power3.out',
        },
        '-=0.6'
      )
      .from(
        testimonialCardRef.value,
        {
          y: 50,
          opacity: 0,
          duration: 0.8,
          ease: 'power3.out',
        },
        '-=0.6'
      )

    // Optional: Animate watermark separately for parallax feel
    if (watermarkRef.value) {
      gsap.from(watermarkRef.value, {
        scrollTrigger: {
          trigger: sectionRef.value,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
        },
        y: 100,
        opacity: 0.2,
        ease: 'none',
      })
    }
  }, sectionRef.value)

  // Update cursor state on scroll (to handle section boundaries)
  window.addEventListener('scroll', updateCursorState, { passive: true })

  // Mouse tracker
  window.addEventListener('mousemove', handleMouseMove)

  // Iniciar auto-avance
  setTimeout(() => {
    startAutoAdvance()
  }, 500)
})

onUnmounted(() => {
  if (ctx) ctx.revert() // Clean up all GSAP animations in this context
  if (progressTween) progressTween.kill()
  window.removeEventListener('scroll', updateCursorState)
  window.removeEventListener('mousemove', handleMouseMove)
})

// Custom Cursor Logic
const isCursorVisible = ref(false)
const cursorState = ref('next') // 'next', 'prev', 'pause'
const lastMouseX = ref(0)
const lastMouseY = ref(0)

const cursorIcon = computed(() => {
  if (cursorState.value === 'pause') return PauseIcon
  if (cursorState.value === 'prev') return ArrowLeft01Icon
  return ArrowRight01Icon
})

const handleMouseMove = (e) => {
  lastMouseX.value = e.clientX
  lastMouseY.value = e.clientY
  updateCursorState()
}

const updateCursorState = () => {
  if (!sectionRef.value) return

  const sectionRect = sectionRef.value.getBoundingClientRect()
  const mouseX = lastMouseX.value
  const mouseY = lastMouseY.value

  // Update opacity based on boundaries
  if (
    !isLinkHovered.value && // Add check here: Don't show if link is hovered
    mouseX >= sectionRect.left &&
    mouseX <= sectionRect.right &&
    mouseY >= sectionRect.top &&
    mouseY <= sectionRect.bottom
  ) {
    isCursorVisible.value = true
  } else {
    isCursorVisible.value = false
    return
  }

  // Move cursor element
  if (cursorRef.value) {
    gsap.to(cursorRef.value, {
      x: mouseX,
      y: mouseY,
      duration: 0.1,
      ease: 'power2.out',
    })
  }

  // Determine state
  // Helper to check hover on quote without event target (using document.elementFromPoint or similar?
  // No, checking elementFromPoint is heavy.
  // Better approach: Calculate relative position.
  // For quote hover, we can check if mouse is within quoteRef rect.)
  let isHoveringQuote = false
  if (quoteRef.value) {
    const quoteRect = quoteRef.value.getBoundingClientRect()
    // Also include authorRef for better UX if needed, but quote is main trigger
    // Actually the listener was on .testimonial-quote container (testimonialCardRef)
    if (testimonialCardRef.value) {
      const cardRect = testimonialCardRef.value.getBoundingClientRect()
      if (
        mouseX >= cardRect.left &&
        mouseX <= cardRect.right &&
        mouseY >= cardRect.top &&
        mouseY <= cardRect.bottom
      ) {
        isHoveringQuote = true
      }
    }
  }

  if (isHoveringQuote) {
    cursorState.value = 'pause'
  } else {
    const relativeX = mouseX - sectionRect.left
    if (relativeX < sectionRect.width / 2) {
      cursorState.value = 'prev'
    } else {
      cursorState.value = 'next'
    }
  }
}

const handleSectionClick = (e) => {
  // Ignore clicks on interactive elements (avatars, buttons)
  if (e.target.closest('button') || e.target.closest('a')) return

  // Custom logic based on cursor state
  if (cursorState.value === 'prev') {
    prevTestimonial()
  } else if (cursorState.value === 'next') {
    nextTestimonial()
  }
  // If pause, do nothing (or toggle play/pause functionality if desired)
}

const isLinkHovered = ref(false)

const handleLinkHover = (isHovering) => {
  isLinkHovered.value = isHovering
  if (isHovering) {
    // Hide custom cursor when hovering link
    isCursorVisible.value = false
    // Ensure text cursor/pointer is shown (handled by CSS on <a> tag)
    // Also pause the carousel
    pauseTimer()
  } else {
    // Re-enable custom cursor visibility if still within section
    updateCursorState()
    resumeTimer()
  }
}
</script>

<style scoped>
.testimonials-section {
  @apply relative py-24 md:py-32 overflow-hidden lg:h-screen;
  background: var(--color-background, var(--color-black));
  cursor: auto; /* Default to auto */
}

/* Only hide cursor when custom cursor is active (desktop) */
.testimonials-section.cursor-hidden {
  cursor: none;
}

/* Restore generic cursor for interactive elements to avoid confusion if needed, 
   but user wants custom experience. Let's keep cursor: none for the general section 
   but restore pointer for specific buttons if they are clickable with the custom cursor. 
*/
button,
a {
  cursor: pointer; /* Default pointer */
}

.testimonials-section.cursor-hidden button,
.testimonials-section.cursor-hidden a {
  cursor: none; /* Hide on interactive elements too when active */
}

/* Custom Cursor */
.custom-cursor {
  @apply fixed pointer-events-none z-50 items-center justify-center hidden lg:flex;
  width: 3.5rem; /* w-14 */
  height: 3.5rem; /* h-14 */
  top: 0;
  left: 0;
  transform: translate(-50%, -50%); /* Center on mouse */
  background: rgba(var(--color-ash), 0.1);
  backdrop-filter: blur(4px);
  border: 1px solid var(--color-electric-purple);
  border-radius: 50%;
  opacity: 0;
  transition:
    opacity 0.2s ease,
    transform 0.1s ease;
}

.custom-cursor--active {
  opacity: 1;
}

.custom-cursor__icon {
  @apply text-[var(--color-text-primary)];
}

.custom-cursor__icon svg {
  @apply w-6 h-6;
}

.testimonials-section__container {
  @apply max-w-5xl mx-auto px-6;
}

/* Header */
.testimonials-section__header {
  @apply text-center mb-16 md:mb-20;
}

.testimonials-section__title {
  @apply text-4xl md:text-5xl font-bold mb-4;
  color: var(--color-text-primary);
}

.testimonials-section__subtitle {
  @apply text-base md:text-lg max-w-lg mx-auto;
  color: var(--color-text-secondary);
}

/* Main Content */
.testimonials-content {
  @apply relative flex flex-col items-center;
  min-height: 400px;
}

/* Watermark Background */
.testimonials-watermark {
  @apply absolute inset-0 flex items-center justify-center pointer-events-none select-none -mt-[20%];
  z-index: 0;
}

.watermark-text {
  @apply font-extrabold tracking-wider;
  font-size: clamp(4rem, 15vw, 12rem);
  color: var(--color-text-primary);
  opacity: 0.02;
  text-transform: uppercase;
  white-space: nowrap;
}

:root:not(.dark) .watermark-text {
  opacity: 0.05;
}

.watermark-logo {
  @apply max-w-full max-h-48 md:max-h-64 object-contain;
  opacity: 0.05;
  filter: grayscale(100%);
}

:root:not(.dark) .watermark-logo {
  opacity: 0.08;
}

/* Quote Content */
.testimonial-quote {
  @apply relative text-center max-w-3xl mx-auto z-10 py-8 md:py-12;
}

.testimonial-quote__text {
  @apply text-xl md:text-2xl lg:text-2xl leading-relaxed mb-8 md:mb-10 font-medium;
  color: var(--color-text-primary);
}

/* Author Info */
.testimonial-author {
  @apply flex flex-wrap items-center justify-center gap-2 text-sm md:text-base;
}

.testimonial-author__name {
  @apply font-semibold tracking-wider uppercase;
  color: var(--color-text-secondary);
  font-size: 0.75rem;
  letter-spacing: 0.15em;
}

.testimonial-author__separator {
  color: var(--color-text-muted);
}

.testimonial-author__role {
  color: var(--color-text-muted);
  font-size: 0.85rem;
}

.testimonial-author__link {
  @apply flex items-center transition-colors duration-300;
  cursor: pointer !important; /* Force pointer even if parent has cursor: none */
  text-decoration: none;
}

.testimonial-author__link:hover .testimonial-author__name,
.testimonial-author__link:hover svg {
  color: #0077b5;
}

/* Ensure svg inherits color transition */
.testimonial-author__link svg {
  @apply text-[var(--color-text-muted)] transition-colors duration-300;
}

/* Read More Button */
.read-more-btn {
  @apply inline-block ml-2 text-sm font-bold transition-colors duration-300;
  cursor: pointer !important;
  color: var(--color-electric-purple);
  background: transparent;
  border: none;
  padding: 0;
}

.read-more-btn:hover {
  text-decoration: underline;
}

/* Avatar Carousel */
.testimonials-carousel {
  @apply flex items-center justify-center gap-4 md:gap-6 mt-12 md:mt-16 z-10;
}

/* Carousel Arrows */
.carousel-arrow {
  @apply w-10 h-10 md:w-12 md:h-12 rounded-full transition-all duration-300 border border-solid;
  background: transparent;
  border-color: var(--color-border);
  color: var(--color-text-secondary);
}

.carousel-arrow:hover:not(:disabled) {
  border-color: var(--color-electric-purple);
  color: var(--color-electric-purple);
  transform: scale(1.05);
}

.carousel-arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.carousel-arrow svg {
  @apply w-5 h-5 md:w-6 md:h-6;
}

/* Avatar Items */
.carousel-avatars {
  @apply flex items-center gap-3 md:gap-4;
}

.avatar-item {
  @apply relative w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer overflow-visible;
  background: var(--color-surface);
  border: 2px solid var(--color-border);
}

.avatar-item:hover {
  border-color: var(--color-text-secondary);
}

.avatar-item--active {
  border-color: transparent; /* Changed from var(--color-electric-purple) to let ring take over */
  transform: scale(1.15);
  /* box-shadow:
    0 0 0 3px rgba(124, 58, 237, 0.2),
    0 0 20px rgba(124, 58, 237, 0.3); */ /* Removed box-shadow to avoid clash with ring */
}

.avatar-item__image {
  @apply w-full h-full rounded-full object-cover;
}

.avatar-item__initials {
  @apply text-sm md:text-base font-semibold;
  color: var(--color-text-secondary);
}

.avatar-item--active .avatar-item__initials {
  color: var(--color-electric-purple);
}

/* Progress Ring */
.avatar-progress-ring {
  @apply absolute inset-0 w-full h-full -rotate-90 pointer-events-none;
}

.avatar-progress-ring__circle {
  fill: none;
  stroke: var(--color-electric-purple);
  stroke-width: 4;
  stroke-linecap: round;
  filter: drop-shadow(0 0 2px var(--color-electric-purple)); /* Add glow effect */
}

@keyframes ring-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(124, 58, 237, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(124, 58, 237, 0);
  }
}

/* Responsive */
@media (max-width: 640px) {
  .testimonial-quote__text {
    @apply text-lg;
  }

  .testimonials-carousel {
    @apply gap-2;
  }

  .carousel-avatars {
    @apply gap-2;
  }

  .avatar-item {
    @apply w-10 h-10;
  }

  .carousel-arrow {
    @apply w-8 h-8;
  }

  .carousel-arrow svg {
    @apply w-4 h-4;
  }

  .watermark-text {
    font-size: 3rem;
  }
}
</style>
